'use client'

import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { useRef } from 'react'

import { cn } from '#lib/cn'

import { textVariants } from './styles'
import {
  type SizeComponentProps,
  type TextElement,
  TextNumeric,
  type TextProps,
  TextSize,
} from './types'

// Mapping from numeric enum values to CSS classes
// Tailwind built-ins: tabular-nums, proportional-nums, slashed-zero, diagonal-fractions
// Custom classes defined in typography.css: font-sups, font-subs, font-numr, font-dnom, font-sinf, font-ss01, font-case
const numericClassMap: Record<TextNumeric, string> = {
  [TextNumeric.Tabular]: 'tabular-nums',
  [TextNumeric.Proportional]: 'proportional-nums',
  [TextNumeric.SlashedZero]: 'slashed-zero',
  [TextNumeric.DiagonalFractions]: 'diagonal-fractions',
  [TextNumeric.Superscript]: 'font-sups',
  [TextNumeric.Subscript]: 'font-subs',
  [TextNumeric.Numerator]: 'font-numr',
  [TextNumeric.Denominator]: 'font-dnom',
  [TextNumeric.ScientificInferiors]: 'font-sinf',
  [TextNumeric.AlternateDigits]: 'font-ss01',
  [TextNumeric.Case]: 'font-case',
}

/**
 * Convert numeric prop value(s) to Tailwind class string
 */
function getNumericClasses(numeric?: TextNumeric | TextNumeric[]): string {
  if (!numeric) {
    return ''
  }
  const values = Array.isArray(numeric) ? numeric : [numeric]
  return values.map((v) => numericClassMap[v]).join(' ')
}

/**
 * Get the default HTML element for a given size
 */
function getDefaultElement(_size?: TextSize): TextElement {
  return 'p'
}

/**
 * Base Text component with CVA variants
 *
 * Note: forwardRef is not needed in React 19 - refs are passed as regular props
 * and useRender merges them automatically via props.ref
 * @see https://base-ui.com/react/utils/use-render#merging-refs
 */
export function Text({
  size,
  weight,
  color,
  as,
  render,
  truncate = false,
  numeric,
  underline,
  className,
  ...props
}: TextProps) {
  const internalRef = useRef<HTMLElement | null>(null)
  const element: TextElement = as ?? getDefaultElement(size)

  // Multi-line truncation. Inline styles instead of `line-clamp-${n}`
  // classes: Tailwind v4 only generates CSS for class names it can find as
  // static literals during scan, so a template-built class name ships with
  // no rule attached and silently fails to clamp.
  const multiLineClampStyle =
    typeof truncate === 'number' && truncate > 1
      ? {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical' as const,
          WebkitLineClamp: truncate,
          overflow: 'hidden',
        }
      : undefined

  return useRender({
    defaultTagName: element,
    props: mergeProps<typeof element>(
      {
        className: cn(
          textVariants({
            color,
            size,
            truncate: truncate === true,
            underline,
            weight,
          }),
          getNumericClasses(numeric),
          className,
        ),
        ...(multiLineClampStyle && { style: multiLineClampStyle }),
      },
      props,
    ),
    ref: internalRef,
    render,
  })
}

Text.displayName = 'Text'

/**
 * XS Text - 11px / 16px line-height
 */
export function XS(props: SizeComponentProps) {
  return <Text size={TextSize.XS} {...props} />
}

/**
 * Small Text - 12px / 18px line-height
 */
export function Small(props: SizeComponentProps) {
  return <Text size={TextSize.Small} {...props} />
}

/**
 * Medium Text - 13px / 20px line-height
 */
export function Medium(props: SizeComponentProps) {
  return <Text size={TextSize.Medium} {...props} />
}

/**
 * Large Text - 14px / 22px line-height (default)
 */
export function Large(props: SizeComponentProps) {
  return <Text size={TextSize.Large} {...props} />
}

/**
 * XL Text - 16px / 24px line-height
 */
export function XL(props: SizeComponentProps) {
  return <Text size={TextSize.XL} {...props} />
}

/**
 * XXL Text - 20px / 28px line-height
 */
export function XXL(props: SizeComponentProps) {
  return <Text size={TextSize.XXL} {...props} />
}

/**
 * XXXL Text - 24px / 32px line-height
 */
export function XXXL(props: SizeComponentProps) {
  return <Text size={TextSize.XXXL} {...props} />
}
